import { type UserSearchItem } from '@atlaskit/smart-common';
import type {
	ActivityEventType,
	ActivityItemEdge,
	RecommendedObject,
	SearchPageNode,
	ZeroQueryDateRange,
	ZeroQueryProduct,
	ZeroQueryType,
} from '@atlassian/search-client';

import type { SearchQueryStringParams } from '../../constants/schemas/query-params';

type ErrorItem = {
	id: string;
	type: string;
};

export const isErrorItem = (item: Item): item is ErrorItem => {
	return item.type === 'error';
};

type LandingItem = {
	id: string;
	type: string;
};

export const isLandingItem = (item: Item): item is LandingItem => {
	return item.type === 'landing';
};

type NoResultsItem = {
	id: string;
	type: string;
};

export const isNoResultsItem = (item: Item): item is NoResultsItem => {
	return item.type === 'no-results';
};

type HeadingItem = {
	id: string;
	type: string;
	data: string;
	includeInAnalytics?: boolean;
};

export const isHeadingItem = (item: Item): item is HeadingItem => {
	return item.type === 'heading';
};

type SkeletonItem = {
	id: string;
	type: string;
};

export const isSkeletonItem = (item: Item): item is SkeletonItem => {
	return item.type === 'skeleton';
};

type RecentQueryItem = {
	id: string;
	type: string;
	data: string;
};

export const isRecentQueryItem = (item: Item): item is RecentQueryItem => {
	return item.type === 'recent-query';
};

type AutocompleteSuggestionItem = {
	id: string;
	type: string;
	data: string;
};

export const isAutocompleteSuggestionItem = (item: Item): item is AutocompleteSuggestionItem => {
	return item.type === 'autocomplete-suggestion';
};

type ZeroQueryItem = {
	id: string;
	type: string;
	data: string;
	product?: ZeroQueryProduct;
	accountId?: string | null;
	avatarUrl?: string | null;
	dateRange?: ZeroQueryDateRange;
	zeroQueryType: ZeroQueryType;
};

export const isZeroQueryItem = (item: Item): item is ZeroQueryItem => {
	return item.type === 'zero-query';
};

export type RecentActivityItem = {
	id: string;
	type: string;
	data: ActivityItemEdge;
};

export const isRecentActivityItem = (item: Item): item is RecentActivityItem => {
	return item.type === 'recent-activity';
};

export type SearchResultItem = {
	id: string;
	type: string;
	data: SearchPageNode;
};

/**
 * Data enriched from the Activity Platform, attached to recommended objects
 * during the enrichment phase. The original `reason` JSON string is never mutated.
 *
 * Add new fields here as needed — the enrichment utility automatically
 * populates them from the first matching activity, and the rendering
 * layer decides how to use them.
 */
export type ActivityEnrichment = {
	timestamp: string;
	eventType: ActivityEventType;
};

/**
 * An enriched version of RecommendedObject that carries optional
 * activity enrichment data from the Activity Platform.
 */
export type EnrichedRecommendedObject = RecommendedObject & {
	activityEnrichment?: ActivityEnrichment;
};

type UserDocumentItems = {
	id: string;
	type: string;
	data: EnrichedRecommendedObject;
};

export const isUserDocumentItem = (item: Item): item is UserDocumentItems => {
	return item.type === 'user-document';
};

export const isSearchResultItem = (item: Item): item is SearchResultItem => {
	return item.type === 'search-result';
};

type PeopleAndTeamsItem = {
	id: string;
	type: string;
	data: UserSearchItem;
};

export const isPeopleAndTeamsItem = (item: Item): item is PeopleAndTeamsItem => {
	return item.type === 'people-and-teams';
};

export type FilterSuggestionGroup = Partial<
	Record<
		Partial<keyof SearchQueryStringParams>,
		{
			filterName: 'app' | 'assignee' | 'project';
			option: { value: string; label: string };
		}
	>
>;

type FilterSuggestionGroupItem = {
	id: string;
	type: 'filter-suggestion';
	data: FilterSuggestionGroup;
};

export const isFilterSuggestionItem = (item: Item): item is FilterSuggestionGroupItem => {
	return item.type === 'filter-suggestion';
};

export type Item =
	| ErrorItem
	| LandingItem
	| NoResultsItem
	| HeadingItem
	| SkeletonItem
	| RecentQueryItem
	| AutocompleteSuggestionItem
	| ZeroQueryItem
	| RecentActivityItem
	| SearchResultItem
	| UserDocumentItems
	| PeopleAndTeamsItem
	| FilterSuggestionGroupItem;
